import '@/css/globals.css';

// Registers the dash-only i18n bundle the host's own chrome translates against.
import '@sparklayer-io/dashboard/runtime';

import { queryClient } from '@sparklayer-io/spark-dash-core/lib/query-client';
import { initColorScheme } from '@sparklayer-io/spark-dash-ui/lib/color-scheme';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel as TanStackQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { createBrowserHistory, createHashHistory, RouterProvider } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initAnalytics } from './lib/analytics';
import { documentTitle } from './lib/document-title';
import { createHostRouter } from './router';

function historyForHost() {
  // Preview builds are served off a branch sub-path with no per-branch SPA fallback, so they need hash routing.
  return import.meta.env.VITE_PREVIEW_HASH_ROUTING === 'true'
    ? createHashHistory()
    : createBrowserHistory();
}

initColorScheme();

const router = createHostRouter({ queryClient, history: historyForHost() });

initAnalytics(router);

// Per-navigation side-effects: document title, Intercom update, HubSpot + GA page tracking.
router.subscribe('onResolved', ({ toLocation }) => {
  const matches = router.state.matches;
  const leaf = matches[matches.length - 1];
  document.title = documentTitle(leaf?.staticData.title);

  window.Intercom('update');
  window._hsq = window._hsq || [];
  const _hsq = window._hsq;
  _hsq.push(['setPath', toLocation.pathname]);
  _hsq.push(['trackPageView']);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'page_view', page: toLocation.pathname });
});

createRoot(document.getElementById('app') as HTMLElement).render(
  <StrictMode>
    <RouterProvider router={router} />
    <TanStackDevtools
      plugins={[
        {
          name: 'TanStack Query',
          render: <TanStackQueryDevtoolsPanel />,
        },
        {
          name: 'TanStack Router',
          render: <TanStackRouterDevtoolsPanel router={router} />,
        },
      ]}
    />
  </StrictMode>,
);
